import {
	GetCustomerUpdatesDocument,
	GetCustomerUpdatesQuery,
} from "__generated__/view/customerUpdate/graphql/queries.generated";
import {
	SaveAllCustomerUpdateCargosDocument,
	SaveAllCustomerUpdateCargosMutation,
	SaveAllCustomerUpdateShipsDocument,
	SaveAllCustomerUpdateShipsMutation,
	SyncAllCustomerUpdatesMutation,
	SyncAllCustomerUpdatesDocument,
	RevertCustomerUpdatesMutation, RevertCustomerUpdatesDocument,
} from "__generated__/view/customerUpdate/graphql/mutations.generated";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";

type CustomerUpdateSnapshot = NonNullable<GetCustomerUpdatesQuery>["customerUpdates"][number];

type Cache = {
	customerUpdate?: CustomerUpdateSnapshot;
	cargosById: Record<number, CustomerUpdateSnapshot["cargos"][number]>;
	shipsById: Record<number, CustomerUpdateSnapshot["ships"][number]>;
	loading: boolean;
}

export type UpdateCargo = {
	cargoId: number;
	statusChangeId?: number | null;
	comment?: string | null;
	estimateTime?: number | null;
}

export type UpdateShip = {
	shipId: number;
	location?: string | null;
}

export type SyncProps = {
	inputs: {
		relationId: number;
		cargoIds: number[];
		shipIds: number[];
	}[];
}

export const useCustomerUpdateState = () => {
	const client = useApolloClient();

	const [ saveAllCargos ] = useMutation<SaveAllCustomerUpdateCargosMutation>(SaveAllCustomerUpdateCargosDocument);
	const [ saveAllShips ] = useMutation<SaveAllCustomerUpdateShipsMutation>(SaveAllCustomerUpdateShipsDocument);

	const [ syncAllCustomerUpdates, { loading: isSyncLoading } ] = useMutation<SyncAllCustomerUpdatesMutation>(SyncAllCustomerUpdatesDocument);

	const [ revertCustomerUpdates, { loading: isRevertLoading } ] = useMutation<RevertCustomerUpdatesMutation>(RevertCustomerUpdatesDocument);

	const [ snapshots, setSnapshots ] = useState<Record<number, Cache>>({});

	const snapshotsRef = useRef<Record<number, Cache>>({});

	const hydrateSnapshots = useCallback((updates: CustomerUpdateSnapshot[]) => {
		setSnapshots(prevState => {
			const next = { ...prevState };

			for (const update of updates) {
				next[update.relationId] = {
					customerUpdate: update,
					cargosById: Object.fromEntries((update.cargos ?? []).map(x => [ x.cargoId, x ])),
					shipsById: Object.fromEntries((update.ships ?? []).map(x => [ x.shipId, x ])),
					loading: false,
				};
			}

			return next;
		});
	}, []);

	const loadAllCustomerUpdates = useCallback(async(relationIds: number[], force: boolean = false, silent: boolean = false) => {
		const relationIdSet = Array.from(new Set(relationIds)).filter(Boolean);

		const toUpdate = relationIdSet.filter(x => {
			const existing = snapshotsRef.current?.[x];
			if (force) {
				return !existing?.loading;
			}
			return !existing?.customerUpdate && !existing?.loading;
		});

		if (toUpdate.length === 0) {
			return;
		}

		// skip loading state if marked as silent
		if (!silent) {
			setSnapshots(prevState => {
				const next = { ...prevState };
				for (const id of toUpdate) {
					const existing = next[id];
					next[id] = {
						customerUpdate: existing?.customerUpdate,
						cargosById: existing?.cargosById ?? {},
						shipsById: existing?.shipsById ?? {},
						loading: true,
					};
				}
				return next;
			});
		}

		const response = await client.query({
			query: GetCustomerUpdatesDocument,
			variables: {
				relationIds: toUpdate,
			},
		});

		hydrateSnapshots(response.data.customerUpdates ?? []);
	}, [ client, hydrateSnapshots ]);

	const isLoading = useCallback((relationId: number) => {
		return snapshots[relationId]?.loading;
	}, [ snapshots ]);

	const getSnapshot = useCallback((relationId: number) => {
		return snapshots[relationId]?.customerUpdate;
	}, [ snapshots ]);

	const getCargo = useCallback((relationId: number, cargoId: number) => {
		return snapshots[relationId]?.cargosById[cargoId];
	}, [ snapshots ]);

	const getShip = useCallback((relationId: number, shipId: number) => {
		return snapshots[relationId]?.shipsById[shipId];
	}, [ snapshots ]);

	const isRelationUpToDate = useCallback((relationId: number) => {
		return snapshots[relationId]?.customerUpdate?.isCommunicated ?? false;
	}, [ snapshots ]);

	const saveCargo = useCallback(async (relationId: number, update: UpdateCargo) => {
		// Optimistically update local state
		setSnapshots(prevState => {
			const current = prevState[relationId];
			if (current == null) {
				return prevState;
			}

			const existing = current.cargosById[update.cargoId];
			const merged = {
				cargoId: update.cargoId,
				statusChangeId: update.statusChangeId ?? existing?.statusChangeId ?? null,
				comment: update.comment ?? existing?.comment ?? null,
				estimateTime: update.estimateTime ?? existing?.estimateTime ?? null,
			};

			return {
				...prevState,
				[relationId]: {
					...current,
					customerUpdate: current.customerUpdate ? { ...current.customerUpdate, isCommunicated: false } : current.customerUpdate,
					cargosById: {
						...current.cargosById,
						[update.cargoId]: merged,
					},
				},
			};
		});

		const response = await saveAllCargos({
			variables: {
				relationId,
				inputs: [ update ],
			},
		});

		const saved = response.data?.saveAllCustomerUpdateCargos ?? [];
		setSnapshots(prevState => {
			const current = prevState[relationId];
			if (current == null) {
				return prevState;
			}

			const next = { ...current.cargosById };
			for (const data of saved) {
				next[data.cargoId] = data;
			}

			return {
				...prevState,
				[relationId]: {
					...current,
					cargosById: next,
				},
			};
		});
		await loadAllCustomerUpdates([ relationId ], true, true);
	}, [ saveAllCargos, loadAllCustomerUpdates ]);

	const saveShip = useCallback(async (relationId: number, update: UpdateShip) => {
		// Optimistically update local state
		setSnapshots(prevState => {
			const current = prevState[relationId];
			if (current == null) {
				return prevState;
			}

			const existing = current.shipsById[update.shipId];
			const merged = {
				shipId: update.shipId,
				location: update.location ?? existing?.location ?? null,
			};

			return {
				...prevState,
				[relationId]: {
					...current,
					customerUpdate: current.customerUpdate ? { ...current.customerUpdate, isCommunicated: false } : current.customerUpdate,
					shipsById: {
						...current.shipsById,
						[update.shipId]: merged,
					},
				},
			};
		});

		const response = await saveAllShips({
			variables: {
				relationId,
				inputs: [ update ],
			},
		});

		const saved = response.data?.saveAllCustomerUpdateShips ?? [];
		setSnapshots(prevState => {
			const current = prevState[relationId];
			if (current == null) {
				return prevState;
			}

			const next = { ...current.shipsById };
			for (const data of saved) {
				next[data.shipId] = data;
			}

			return {
				...prevState,
				[relationId]: {
					...current,
					shipsById: next,
				},
			};
		});
		await loadAllCustomerUpdates([ relationId ], true, true);
	}, [ saveAllShips, loadAllCustomerUpdates ]);

	const syncAllLatest = useCallback(async(props: SyncProps) => {
		const response = await syncAllCustomerUpdates({
			variables: {
				inputs: props.inputs,
			},
		});

		const updated = response.data?.syncCustomerUpdates ?? [];
		hydrateSnapshots(updated);
	}, [ hydrateSnapshots, syncAllCustomerUpdates ]);

	const revertAllCustomerUpdates = useCallback(async() => {
		await revertCustomerUpdates();

		setSnapshots({});
		snapshotsRef.current = {};
	}, [ revertCustomerUpdates ]);

	useEffect(() => {
		snapshotsRef.current = snapshots;
	}, [ snapshots ]);

	return useMemo(() => ({
		loadAllCustomerUpdates,
		isLoading,
		getSnapshot,
		getCargo,
		getShip,
		isRelationUpToDate,
		saveCargo,
		saveShip,
		syncAllLatest,
		isSyncLoading,
		revertAllCustomerUpdates,
		isRevertLoading,
	}), [
		loadAllCustomerUpdates,
		isLoading,
		getSnapshot,
		getCargo,
		getShip,
		isRelationUpToDate,
		saveCargo,
		saveShip,
		syncAllLatest,
		isSyncLoading,
		revertAllCustomerUpdates,
		isRevertLoading,
	]);
};
